import { useEffect, useRef } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { departmentName, SORT_OPTIONS, type SortOption } from "@neus/shared";
import { useFacets, useProducts } from "../api/hooks";
import { track } from "../api/track";
import { FacetSidebar } from "../components/product/FacetSidebar";
import { Pagination } from "../components/product/bits";
import { ProductGrid, ProductGridSkeleton } from "../components/product/ProductCard";
import styles from "./pages.module.css";

const SORT_LABELS: Record<SortOption, string> = {
  popular: "Popular",
  relevance: "Best match",
  "price-asc": "Price: low to high",
  "price-desc": "Price: high to low",
  "name-asc": "Name: A–Z",
  newest: "Newest",
};

interface Props {
  title: string;
  department?: string;
  brand?: string;
  q?: string;
}

function ProductList({ title, department, brand, q }: Props) {
  const [params, setParams] = useSearchParams();
  // Searches default to best match; "Best match" is only offered for searches.
  const defaultSort: SortOption = q ? "relevance" : "popular";
  const sortOptions = SORT_OPTIONS.filter((s) => s !== "relevance" || q);
  const requested = params.get("sort") as SortOption | null;
  const sort = requested && sortOptions.includes(requested) ? requested : defaultSort;
  const page = Math.max(1, Number(params.get("page")) || 1);
  const inStock = params.get("inStock") === "true";

  const { data, isLoading, isError, isPlaceholderData, refetch } = useProducts({
    department,
    brand,
    q,
    sort,
    page,
    pageSize: 24,
    inStock: inStock ? "true" : undefined,
  });

  // Report each search once, when its first page of results appears, so re-sorting, filtering,
  // paging and background refetches don't count as new searches.
  const reportedSearch = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!q || !data || isPlaceholderData || page !== 1 || reportedSearch.current === q) return;
    reportedSearch.current = q;
    track({ type: "search", term: q, results: data.total });
  }, [q, data, isPlaceholderData, page]);

  const update = (patch: Record<string, string | undefined>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    if (!("page" in patch)) next.delete("page");
    setParams(next, { replace: !("page" in patch) });
  };

  return (
    <>
      <div className="page-header">
        <div className="container">
          <div className="breadcrumbs">
            <Link to="/">Home</Link> / <Link to="/products">Products</Link>
            {title !== "All Products" && <> / {title}</>}
          </div>
          <h1>{title}</h1>
        </div>
      </div>

      <div className={`container ${styles.listLayout}`}>
        <FacetSidebar inStock={inStock} onInStock={(v) => update({ inStock: v ? "true" : undefined })} />

        <section aria-live="polite" aria-busy={isLoading || isPlaceholderData}>
          <div className={styles.toolbar}>
            <span className="muted">{data ? `${data.total} product${data.total === 1 ? "" : "s"}` : " "}</span>
            <label>
              <span className="visually-hidden">Sort by</span>
              <select
                className="select"
                value={sort}
                onChange={(e) => update({ sort: e.target.value === defaultSort ? undefined : e.target.value })}
              >
                {sortOptions.map((s) => (
                  <option key={s} value={s}>
                    {SORT_LABELS[s]}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {isError ? (
            <div className="notice notice--warn">
              We couldn&rsquo;t load products right now.{" "}
              <button type="button" className="btn btn--ghost" onClick={() => refetch()}>
                Try again
              </button>
            </div>
          ) : isLoading || !data ? (
            <ProductGridSkeleton />
          ) : data.items.length === 0 ? (
            <div className={styles.empty}>
              <h2>No products found</h2>
              <p>Try a different search or browse all products.</p>
              <Link to="/products" className="btn">
                View all products
              </Link>
            </div>
          ) : (
            <div style={{ opacity: isPlaceholderData ? 0.6 : 1, transition: "opacity .15s" }}>
              <ProductGrid products={data.items} />
              <Pagination
                page={data.page}
                pageSize={data.pageSize}
                total={data.total}
                onPage={(p) => update({ page: p > 1 ? String(p) : undefined })}
              />
            </div>
          )}
        </section>
      </div>
    </>
  );
}

export function AllProductsPage() {
  return <ProductList title="All Products" />;
}

export function DepartmentPage({ slug }: { slug: string }) {
  return <ProductList key={slug} title={departmentName(slug)} department={slug} />;
}

export function BrandPage() {
  const { slug = "" } = useParams();
  const { data } = useFacets();
  const name = data?.brands.find((b) => b.slug === slug)?.name ?? slug.replace(/-/g, " ");
  return <ProductList key={slug} title={name} brand={slug} />;
}

export function SearchPage() {
  const [params] = useSearchParams();
  const q = params.get("q") ?? "";
  return <ProductList key={q} title={q ? `Search results for “${q}”` : "Search"} q={q} />;
}
